import React, { useEffect, useMemo, useState } from "react";
import PACK_JAPON_PUBLIC_FALLBACK from "../../data/packJaponPublicFallback";
import { Box, Typography, Button, Chip, CircularProgress } from "@mui/material";
import PackConfirmModal from "../PackConfirmModal";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesomeOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

/**
 * PacksSection
 *
 * Sección destacada del dashboard para los "packs" (bundles que
 * incluyen una guía prebuilt + stickers personalizables + páginas
 * extra en el editor). El primer pack es Pack Japón.
 *
 * Se encarga de:
 *   - Cargar los packs del backend (con flags de acceso del usuario).
 *   - Renderizar cada pack como una tarjeta grande "marketing-style".
 *   - Lanzar la compra vía Stripe Checkout (purchasePack).
 *   - Mostrar estado cuando ya lo tienes (por pack o por Pro).
 */

const PACK_ICON_MAP = {
	guide: MenuBookOutlinedIcon,
	stickers: AutoAwesomeIcon,
	stickerPack: CollectionsOutlinedIcon,
	pages: StickyNote2OutlinedIcon,
};

const formatPrice = (price, currency = "EUR") => {
	if (price == null) return "";
	try {
		return new Intl.NumberFormat("es-ES", {
			style: "currency",
			currency,
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(price);
	} catch {
		return `${price.toFixed(2)} €`;
	}
};

const PackCard = ({
	pack,
	onPurchase,
	purchasing,
	onLogin,
	isLoggedIn,
	onOpenGuide,
	onEditGuide,
}) => {
	const { marketing = {}, title, subtitle, price, currency } = pack;
	const accent = marketing.accent || "#ef4444";
	const gradientBg = `linear-gradient(135deg, ${accent}22 0%, ${accent}0a 100%)`;
	const heroImage = marketing.heroImage;
	const highlights = marketing.highlights || [];
	const owned = !!pack.isOwned;
	const unlockedByPro = !!pack.isUnlockedByPro;
	const hasAccess = !!pack.hasAccess;
	const [showConfirm, setShowConfirm] = useState(false);

	const handleCtaClick = () => {
		if (!isLoggedIn) {
			onLogin?.();
			return;
		}
		setShowConfirm(true);
	};

	const handleConfirmPurchase = () => {
		setShowConfirm(false);
		onPurchase?.(pack);
	};

	return (
		<>
		<Box
			sx={{
				position: "relative",
				display: "flex",
				flexDirection: { xs: "column", md: "row" },
				alignItems: "stretch",
				borderRadius: 4,
				overflow: "hidden",
				border: "1px solid #ececf4",
				backgroundColor: "#fff",
				boxShadow: "0 24px 60px -30px rgba(15, 23, 42, 0.22)",
				transition: "transform .2s ease, box-shadow .2s ease",
				"&:hover": {
					transform: { md: "translateY(-2px)" },
					boxShadow: "0 30px 70px -30px rgba(15, 23, 42, 0.28)",
				},
			}}
		>
			{/* Hero image (izq en desktop, arriba en mobile) */}
			<Box
				sx={{
					position: "relative",
					flex: { xs: "0 0 auto", md: "0 0 44%" },
					minWidth: 0,
					minHeight: { xs: 200, md: 300 },
					background: gradientBg,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					p: { xs: 2.5, md: 3 },
				}}
			>
				{/* Badge con bandera/acento */}
				<Box
					sx={{
						position: "absolute",
						top: 16,
						left: 16,
						zIndex: 2,
						display: "flex",
						alignItems: "center",
						gap: 0.75,
						backgroundColor: "#fff",
						borderRadius: 999,
						px: 1.5,
						py: 0.5,
						boxShadow: "0 4px 14px -4px rgba(15, 23, 42, 0.18)",
					}}
				>
					{marketing.flag && (
						<Typography sx={{ fontSize: 18, lineHeight: 1 }}>
							{marketing.flag}
						</Typography>
					)}
					<Typography
						sx={{
							fontSize: 12,
							fontWeight: 700,
							letterSpacing: 0.3,
							color: accent,
							textTransform: "uppercase",
						}}
					>
						Pack
					</Typography>
				</Box>

				{/* Chip de estado arriba-dcha */}
				{hasAccess && (
					<Chip
						icon={
							unlockedByPro ? (
								<WorkspacePremiumIcon sx={{ fontSize: 16 }} />
							) : (
								<CheckCircleRoundedIcon sx={{ fontSize: 16 }} />
							)
						}
						label={unlockedByPro ? "Incluido en Pro" : "Ya lo tienes"}
						sx={{
							position: "absolute",
							top: 16,
							right: 16,
							zIndex: 2,
							backgroundColor: "#0f172a",
							color: "#fff",
							fontWeight: 600,
							".MuiChip-icon": { color: "#fff" },
						}}
						size="small"
					/>
				)}

				{heroImage ? (
					<Box
						component="img"
						src={heroImage}
						alt={title}
						sx={{
							position: "relative",
							zIndex: 1,
							display: "block",
							width: "100%",
							height: "auto",
							maxWidth: "100%",
							maxHeight: { xs: 280, md: 400 },
							objectFit: "contain",
							objectPosition: "center center",
							filter: "drop-shadow(0 12px 24px rgba(15,23,42,0.18))",
							pointerEvents: "none",
						}}
					/>
				) : (
					<Typography
						sx={{
							fontSize: { xs: 96, md: 140 },
							lineHeight: 1,
						}}
					>
						{marketing.flag || "🎁"}
					</Typography>
				)}
			</Box>

			{/* Contenido (der en desktop, abajo en mobile) */}
			<Box
				sx={{
					flex: "1 1 auto",
					p: { xs: 3, md: 4 },
					display: "flex",
					flexDirection: "column",
					gap: 2,
					minWidth: 0,
				}}
			>
				<Box>
					<Typography
						variant="h4"
						sx={{
							fontWeight: 800,
							color: "#0f172a",
							fontSize: { xs: "1.5rem", md: "1.875rem" },
							letterSpacing: "-0.02em",
							mb: 0.5,
						}}
					>
						{title}
					</Typography>
					{subtitle && (
						<Typography
							sx={{
								color: "#64748B",
								fontSize: { xs: "0.875rem", sm: "0.95rem" },
								lineHeight: 1.45,
							}}
						>
							{subtitle}
						</Typography>
					)}
				</Box>

				{/* Tagline */}
				{marketing.tagline && (
					<Typography
						sx={{
							fontSize: "0.95rem",
							color: "#475569",
							fontStyle: "italic",
						}}
					>
						{marketing.tagline}
					</Typography>
				)}

				{/* Highlights */}
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 1.25,
						mt: 0.5,
					}}
				>
					{highlights.map((h, idx) => {
						const Icon = PACK_ICON_MAP[h.icon] || CheckCircleRoundedIcon;
						return (
							<Box
								key={idx}
								sx={{
									display: "flex",
									alignItems: "flex-start",
									gap: 1.25,
								}}
							>
								<Box
									sx={{
										flexShrink: 0,
										width: 32,
										height: 32,
										borderRadius: "50%",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										backgroundColor: `${accent}1a`,
										color: accent,
									}}
								>
									<Icon sx={{ fontSize: 18 }} />
								</Box>
								<Box sx={{ minWidth: 0 }}>
									<Typography
										sx={{
											fontWeight: 700,
											color: "#0f172a",
											fontSize: "0.95rem",
											lineHeight: 1.3,
										}}
									>
										{h.title}
									</Typography>
									{h.description && (
										<Typography
											sx={{
												color: "#64748B",
												fontSize: "0.825rem",
												lineHeight: 1.45,
											}}
										>
											{h.description}
										</Typography>
									)}
								</Box>
							</Box>
						);
					})}
				</Box>

				{/* CTA row */}
				<Box
					sx={{
						mt: "auto",
						pt: 2,
						display: "flex",
						flexDirection: { xs: "column", sm: "row" },
						alignItems: { xs: "stretch", sm: "center" },
						justifyContent: "space-between",
						gap: 2,
					}}
				>
					<Box>
						{!hasAccess && (
							<Typography
								sx={{
									fontWeight: 800,
									color: "#0f172a",
									fontSize: { xs: "1.75rem", md: "2rem" },
									lineHeight: 1,
								}}
							>
								{formatPrice(price, currency)}
							</Typography>
						)}
						{!hasAccess && (
							<Typography
								sx={{
									color: "#94a3b8",
									fontSize: "0.75rem",
									mt: 0.5,
								}}
							>
								Pago único · Acceso para siempre
							</Typography>
						)}
						{hasAccess && (
							<Typography
								sx={{
									color: "#0f172a",
									fontWeight: 600,
									fontSize: "0.95rem",
								}}
							>
								{unlockedByPro
									? "Incluido con tu plan Pro"
									: "¡Ya tienes acceso a este pack!"}
							</Typography>
						)}
					</Box>

					{hasAccess ? (
						<Box
							sx={{
								display: "flex",
								gap: 1.5,
								flexDirection: { xs: "column", sm: "row" },
							}}
						>
							<Button
								variant="outlined"
								size="large"
								onClick={() => {
									const firstGuide = pack.entitlements?.prebuiltGuides?.[0];
									if (firstGuide) onOpenGuide?.(firstGuide);
								}}
								endIcon={<ArrowForwardRoundedIcon />}
								sx={{
									borderColor: "#cbd5e1",
									color: "#0f172a",
									fontWeight: 700,
									textTransform: "none",
									borderRadius: 2,
									px: 3,
									py: 1.25,
									"&:hover": {
										borderColor: "#94a3b8",
										backgroundColor: "#f8fafc",
									},
								}}
							>
								Ver la guía
							</Button>
							<Button
								variant="contained"
								size="large"
								onClick={() => {
									const firstGuide = pack.entitlements?.prebuiltGuides?.[0];
									if (firstGuide) onEditGuide?.(firstGuide);
								}}
								endIcon={<ArrowForwardRoundedIcon />}
								sx={{
									backgroundColor: accent,
									color: "#fff",
									fontWeight: 700,
									textTransform: "none",
									borderRadius: 2,
									px: 3,
									py: 1.25,
									boxShadow: `0 10px 20px -8px ${accent}88`,
									"&:hover": {
										backgroundColor: accent,
										filter: "brightness(0.95)",
									},
								}}
							>
								Editar guía
							</Button>
						</Box>
					) : (
						<Button
							variant="contained"
							size="large"
							onClick={handleCtaClick}
							disabled={purchasing || !pack.isAvailableForPurchase}
							endIcon={
								purchasing ? (
									<CircularProgress size={16} sx={{ color: "#fff" }} />
								) : (
									<ArrowForwardRoundedIcon />
								)
							}
							sx={{
								backgroundColor: accent,
								color: "#fff",
								fontWeight: 700,
								textTransform: "none",
								borderRadius: 2,
								px: 3,
								py: 1.25,
								boxShadow: `0 10px 20px -8px ${accent}88`,
								"&:hover": {
									backgroundColor: accent,
									filter: "brightness(0.95)",
								},
							}}
						>
							Conseguir pack
						</Button>
					)}
				</Box>
			{marketing.digitalOnlyNotice && (
				<Typography
					component="p"
					sx={{
						fontSize: { xs: "0.78rem", sm: "0.82rem" },
						color: "#94a3b8",
						lineHeight: 1.45,
						m: 0,
						p: 1.25,
						borderRadius: 1.5,
						backgroundColor: "rgba(15, 23, 42, 0.04)",
						border: "1px solid rgba(148, 163, 184, 0.35)",
					}}
				>
					{marketing.digitalOnlyNotice}
				</Typography>
			)}
		</Box>
	</Box>

		<PackConfirmModal
			open={showConfirm}
			onClose={() => setShowConfirm(false)}
			onConfirm={handleConfirmPurchase}
			loading={purchasing}
			packTitle={title || "Pack"}
			packPrice={formatPrice(price, currency)}
		/>
		</>
	);
};

const PacksSection = ({
	onPurchasePack,
	onLogin,
	isLoggedIn,
	getPacks,
	purchasingPackId = null,
	onOpenGuide,
	onEditGuide,
}) => {
	const [packs, setPacks] = useState([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			setLoading(true);
			try {
				const list = await getPacks?.();
				if (!cancelled) setPacks(list || []);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [getPacks]);

	// Misma tarjeta para invitados y logueados: si el API no devuelve nada, mostramos fallback local.
	const displayPacks = useMemo(() => {
		if (packs.length > 0) return packs;
		if (!loading) return [PACK_JAPON_PUBLIC_FALLBACK];
		return [];
	}, [loading, packs]);

	return (
		<Box sx={{ mb: 5 }}>
			<Box sx={{ mb: 3 }}>
				<Typography
					variant="h5"
					sx={{
						fontWeight: 700,
						color: "#1a1a2e",
						fontSize: { xs: "1.25rem", md: "1.5rem" },
						letterSpacing: "-0.02em",
						mb: 1,
					}}
				>
					Packs destacados
				</Typography>
				<Typography
					sx={{
						color: "#64748B",
						fontSize: { xs: "0.875rem", sm: "0.938rem" },
						lineHeight: 1.45,
					}}
				>
					Packs con todo lo que necesitas para un destino: guía completa,
					stickers personalizables y páginas extra para el editor.
				</Typography>
			</Box>

			<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
				{loading ? (
					<Box
						sx={{
							py: 6,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<CircularProgress size={28} />
					</Box>
				) : (
					displayPacks.map((pack) => (
						<PackCard
							key={pack.id}
							pack={pack}
							onPurchase={onPurchasePack}
							onLogin={onLogin}
							isLoggedIn={isLoggedIn}
							purchasing={purchasingPackId === pack.id}
							onOpenGuide={onOpenGuide}
							onEditGuide={onEditGuide}
						/>
					))
				)}
			</Box>
		</Box>
	);
};

export default PacksSection;
